import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button, Skeleton } from "@nexus/ui";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import type { CreativeImportRowDto, MarketplaceFulfillmentStatus } from "@nexus/contracts";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useMarketplaceAdminCapability } from "../hooks/useMarketplaceAdminCapability";
import {
  adminOrderImportQueryOptions,
  adminVendorsQueryOptions,
  useBulkSetOrderImportNewVendorMapping,
  useBulkSetOrderImportVendorMapping,
  useAcceptPrefilledOrderImportDates,
  useAcceptOrderImportWarnings,
  useCommitOrderImport,
  useParseOrderImport,
  useUpdateOrderImportRow,
} from "@/lib/queries";
import { ImportVendorMappingDialog } from "../components/order-import/ImportVendorMappingDialog";
import { ImportRowsTable } from "../components/order-import/ImportRowsTable";
import { ImportSummaryPanel } from "../components/order-import/ImportSummaryPanel";
import { ImportUploadCard } from "../components/order-import/ImportUploadCard";
import { ImportWorkflowSteps } from "../components/order-import/ImportWorkflowSteps";
import {
  buildVendorLabelGroups,
  computeImportReadiness,
  filterIssuesOnCommittingRows,
} from "../components/order-import/import-readiness";
import { buildDefaultOpportunitiesLinkSearch } from "@/features/opportunities/constants/opportunity-filters";

export function MarketplaceOrderImportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canMarketplaceAdmin = useMarketplaceAdminCapability();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const [importId, setImportId] = useState<string | null>(null);
  const [vendorMappingOpen, setVendorMappingOpen] = useState(false);

  const parseMutation = useParseOrderImport();
  const updateRowMutation = useUpdateOrderImportRow();
  const bulkVendorMutation = useBulkSetOrderImportVendorMapping();
  const bulkNewVendorMutation = useBulkSetOrderImportNewVendorMapping();
  const acceptPrefilledMutation = useAcceptPrefilledOrderImportDates();
  const acceptWarningsMutation = useAcceptOrderImportWarnings();
  const commitMutation = useCommitOrderImport();

  const tenantId = activeTenantId ?? "";
  const {
    data: previewData,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = useQuery({
    ...adminOrderImportQueryOptions(tenantId, importId),
    enabled: Boolean(tenantId && importId),
  });
  const preview =
    previewData ??
    (parseMutation.data?.id === importId ? parseMutation.data : undefined);
  const { data: vendors = [] } = useQuery({
    ...adminVendorsQueryOptions(),
    enabled: canMarketplaceAdmin,
  });

  const vendorLabelGroups = useMemo(
    () => buildVendorLabelGroups(preview?.rows ?? []),
    [preview?.rows],
  );
  const readiness = useMemo(
    () => computeImportReadiness(preview, vendorLabelGroups),
    [preview, vendorLabelGroups],
  );
  const batchColumnDetected = useMemo(
    () =>
      !preview?.issues.some(
        (issue) => issue.code === "UNMAPPED_COLUMN" && issue.rowId === null,
      ),
    [preview?.issues],
  );

  const canParse = canMarketplaceAdmin && Boolean(tenantId);
  const mappingBusy = bulkVendorMutation.isPending || bulkNewVendorMutation.isPending;

  function handleFileSelect(file: File) {
    if (!tenantId) return;
    parseMutation.mutate(
      { tenantId, file },
      {
        onSuccess: (data) => {
          setImportId(data.id);
          const groups = buildVendorLabelGroups(data.rows);
          const hasUnmappedLabels = groups.some(
            (group) => !group.mappedVendorId && !group.pendingVendorContactEmail,
          );
          if (hasUnmappedLabels) {
            setVendorMappingOpen(true);
          }
        },
      },
    );
  }

  function handleRowPatch(row: CreativeImportRowDto, patch: {
    vendorId?: string | null;
    hook?: string | null;
    clientStartDate?: string | null;
    expectedDeliveryDate?: string | null;
    fulfillmentStatus?: MarketplaceFulfillmentStatus | null;
  }) {
    if (!importId || !tenantId) return;
    updateRowMutation.mutate({
      tenantId,
      importId,
      rowId: row.id,
      payload: patch,
    });
  }

  function handleBulkVendorMap(vendorLabel: string, vendorId: string) {
    if (!importId || !tenantId) return;
    bulkVendorMutation.mutate({
      tenantId,
      importId,
      payload: { vendorLabel, vendorId },
    });
  }

  function handleBulkNewVendorMap(vendorLabel: string, contactEmail: string) {
    if (!importId || !tenantId) return;
    bulkNewVendorMutation.mutate({
      tenantId,
      importId,
      payload: { vendorLabel, contactEmail },
    });
  }

  function handleCommit() {
    if (!importId || !tenantId) return;
    commitMutation.mutate(
      { tenantId, importId },
      {
        onSuccess: (data) => {
          const setId = data.opportunitySetIds[0];
          void navigate({
            to: "/opportunities",
            search: buildDefaultOpportunitiesLinkSearch({
              tab: "completed_scripts",
              ...(setId ? { setId } : {}),
            }),
          });
        },
      },
    );
  }

  function handleAcceptPrefilledDates() {
    if (!importId || !tenantId) return;
    acceptPrefilledMutation.mutate({ tenantId, importId });
  }

  function handleAcceptWarnings() {
    if (!importId || !tenantId) return;
    acceptWarningsMutation.mutate({ tenantId, importId });
  }

  function handleReplaceFile() {
    setImportId(null);
  }

  const blockingIssues =
    filterIssuesOnCommittingRows(
      preview,
      preview?.issues.filter((issue) => issue.severity === "BLOCKING") ?? [],
    );
  const warningIssues = preview?.issues.filter((issue) => issue.severity !== "BLOCKING") ?? [];

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-4 sm:p-6">
      <header className="flex flex-col gap-3">
        <Button variant="ghost" size="sm" className="-ml-2 h-7 w-fit px-2" asChild>
          <Link to="/marketplace">
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            {t("marketplace.admin.import.backToMarketplace")}
          </Link>
        </Button>
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("marketplace.admin.import.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("marketplace.admin.import.subtitle")}
          </p>
        </div>
      </header>

      {!importId ? (
        <ImportUploadCard
          canParse={canParse}
          isParsing={parseMutation.isPending}
          onFileSelect={handleFileSelect}
        />
      ) : isPreviewLoading && !preview ? (
        <div className="space-y-4">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-[65vh] w-full rounded-xl" />
        </div>
      ) : isPreviewError || !preview ? (
        <section className="rounded-xl border border-border/60 bg-card p-6 text-sm text-muted-foreground">
          {t("marketplace.admin.import.previewLoadError")}
        </section>
      ) : (
        <div className="flex min-h-[calc(100svh-8rem)] flex-col gap-6">
          <ImportWorkflowSteps readiness={readiness} />

          <div className="grid min-h-0 min-w-0 flex-1 gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
            <div className="flex min-h-0 min-w-0 flex-col gap-4">
              <div className="shrink-0 space-y-4">
                {readiness.unmappedDirectRows > 0 ? (
                  <section className="flex items-start gap-2 rounded-xl border border-status-warning/30 bg-status-warning-subtle/40 p-4 text-sm text-status-warning">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <p>{t("marketplace.admin.import.directVendorRowsHint", { count: readiness.unmappedDirectRows })}</p>
                  </section>
                ) : null}
              </div>

              <ImportRowsTable
                className="min-h-0"
                rows={preview.rows}
                vendorLabelGroups={vendorLabelGroups}
                vendors={vendors}
                batchColumnDetected={batchColumnDetected}
                onRowPatch={handleRowPatch}
              />
            </div>

            <ImportSummaryPanel
              preview={preview}
              readiness={readiness}
              blockingIssues={blockingIssues}
              warningIssues={warningIssues}
              hasVendorMapping={vendorLabelGroups.length > 0}
              isCommitting={commitMutation.isPending}
              onOpenVendorMapping={() => setVendorMappingOpen(true)}
              onAcceptPrefilledDates={handleAcceptPrefilledDates}
              onAcceptWarnings={handleAcceptWarnings}
              acceptPrefilledPending={acceptPrefilledMutation.isPending}
              acceptWarningsPending={acceptWarningsMutation.isPending}
              onCommit={handleCommit}
              onReplaceFile={handleReplaceFile}
            />
          </div>

          <ImportVendorMappingDialog
            open={vendorMappingOpen}
            onOpenChange={setVendorMappingOpen}
            vendorLabelGroups={vendorLabelGroups}
            vendors={vendors}
            mappingBusy={mappingBusy}
            onMapExisting={handleBulkVendorMap}
            onMapNew={handleBulkNewVendorMap}
          />
        </div>
      )}
    </div>
  );
}
